import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { getLayout, createLayout, updateLayout, getAvailableFields } from '../../services/api';

// Fabric.js è ora caricato globalmente tramite <script> in index.html
const fabric = window.fabric;

function LayoutEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);

  const [layoutName, setLayoutName] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [availableModules, setAvailableModules] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: 210 * (96 / 25.4),
      height: 297 * (96 / 25.4),
      backgroundColor: '#f0f0f0',
    });
    fabricCanvasRef.current = canvas;

    // Personalizzazione dei controlli per renderli più visibili
    fabric.Object.prototype.transparentCorners = false;
    fabric.Object.prototype.cornerColor = '#007bff';
    fabric.Object.prototype.cornerStyle = 'circle';
    fabric.Object.prototype.borderColor = '#007bff';
    fabric.Object.prototype.cornerSize = 10;
    fabric.Object.prototype.borderScaleFactor = 2;

    const updateSelection = (e) => {
      setSelectedItem(canvas.getActiveObject());
    };

    canvas.on('selection:created', updateSelection);
    canvas.on('selection:updated', updateSelection);
    canvas.on('selection:cleared', () => setSelectedItem(null));
    canvas.on('object:modified', updateSelection);

    if (id) {
      getLayout(id).then(response => {
        const layout = response.data;
        setLayoutName(layout.nome);
        setSelectedModule(layout.modulo);
        const config = layout.configurazione || {};
        if (config.fabricJSON) {
          canvas.loadFromJSON(config.fabricJSON, () => {
            canvas.renderAll();
            if (config.backgroundImage) {
              setBackgroundImage(config.backgroundImage);
              fabric.Image.fromURL(config.backgroundImage, (img) => {
                canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
                  scaleX: canvas.width / img.width,
                  scaleY: canvas.height / img.height,
                });
              });
            }
          });
        }
      }).catch(error => console.error("Errore nel caricamento del layout", error));
    }

    return () => {
      canvas.dispose();
    };
  }, [id]);

  useEffect(() => {
    getAvailableFields().then(response => {
      setAvailableModules(Object.keys(response.data));
    }).catch(error => console.error("Errore nel caricamento dei moduli", error));
  }, []);

  useEffect(() => {
    if (selectedModule) {
      getAvailableFields().then(response => {
        setAvailableFields(response.data[selectedModule] || []);
      }).catch(error => console.error(`Errore nel caricamento dei campi per il modulo ${selectedModule}`, error));
    } else {
      setAvailableFields([]);
    }
  }, [selectedModule]);

  const handleDrop = (e) => {
    e.preventDefault();
    const fieldName = e.dataTransfer.getData("fieldName");
    const canvas = fabricCanvasRef.current;
    const canvasRect = canvas.upperCanvasEl.getBoundingClientRect();
    
    const left = e.clientX - canvasRect.left;
    const top = e.clientY - canvasRect.top;

    if (fieldName) {
      const text = new fabric.IText(fieldName, {
        left,
        top,
        fontFamily: 'Helvetica',
        fontSize: 12,
        fill: '#000000',
        dbFieldName: fieldName,
      });
      canvas.add(text);
    }
  };

  const addRectangle = () => {
    const canvas = fabricCanvasRef.current;
    const rect = new fabric.Rect({
      left: 10,
      top: 10,
      width: 100,
      height: 50,
      fill: 'transparent',
      stroke: '#000000',
      strokeWidth: 1,
    });
    canvas.add(rect);
  };

  const addLine = () => {
    const canvas = fabricCanvasRef.current;
    const line = new fabric.Line([ 50, 100, 200, 100 ], {
        left: 10,
        top: 10,
        stroke: '#000000',
        strokeWidth: 2,
    });
    canvas.add(line);
  };

  const addImage = (e) => {
    const file = e.target.files[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = (f) => {
            fabric.Image.fromURL(f.target.result, (img) => {
                img.set({ left: 10, top: 10, scaleX: 0.2, scaleY: 0.2 });
                fabricCanvasRef.current.add(img);
            });
        };
        reader.readAsDataURL(file);
    }
  };

  const handleItemChange = (e) => {
    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (!activeObject) return;
    
    const { name, value } = e.target;
    
    if (name === 'width' || name === 'height' || name === 'fontSize' || name === 'strokeWidth') {
        const val = parseFloat(value);
        if (isNaN(val)) return;

        if (name === 'width') {
            activeObject.scaleToWidth(val);
        } else if (name === 'height') {
            activeObject.scaleToHeight(val);
        } else {
            activeObject.set(name, val);
        }
    } else {
        activeObject.set(name, value);
    }
    
    fabricCanvasRef.current.renderAll();
    setSelectedItem(activeObject.toObject(['dbFieldName']));
  };

  const handleBackgroundImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (f) => {
        const dataUrl = f.target.result;
        setBackgroundImage(dataUrl);
        fabric.Image.fromURL(dataUrl, (img) => {
          fabricCanvasRef.current.setBackgroundImage(img, fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current), {
            scaleX: fabricCanvasRef.current.width / img.width,
            scaleY: fabricCanvasRef.current.height / img.height,
          });
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    const canvas = fabricCanvasRef.current;
    const config = {
      fabricJSON: canvas.toObject(['dbFieldName']),
      backgroundImage: backgroundImage,
    };
    const layoutData = {
      nome: layoutName,
      modulo: selectedModule,
      configurazione: config,
    };

    try {
      if (id) {
        await updateLayout(id, layoutData);
      } else {
        await createLayout(layoutData);
      }
      navigate('/layouts');
    } catch (error) {
      console.error("Errore nel salvataggio del layout", error);
    }
  };

  return (
    <Container fluid>
      <h1>{id ? 'Modifica' : 'Nuovo'} Layout</h1>
      <Row>
        <Col md={2}>
          <h5>Campi Disponibili</h5>
          <Form.Group className="mb-3">
            <Form.Label>Modulo</Form.Label>
            <Form.Select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)} disabled={!!id}>
              <option value="">Seleziona un modulo</option>
              {availableModules.map(mod => <option key={mod} value={mod}>{mod}</option>)}
            </Form.Select>
          </Form.Group>
          <div onDragStart={(e) => e.dataTransfer.setData("fieldName", e.target.textContent)}>
            {availableFields.map((field) => (
              <div key={field} draggable="true" style={{ padding: '8px', border: '1px solid #ccc', marginBottom: '4px', backgroundColor: 'white', cursor: 'move' }}>
                {field}
              </div>
            ))}
          </div>
          <hr />
          <h5>Elementi Grafici</h5>
          <Button variant="outline-secondary" size="sm" className="w-100 mb-2" onClick={addRectangle}>
                          Aggiungi Rettangolo
                      </Button>
                      <Button variant="outline-secondary" size="sm" className="w-100 mb-2" onClick={addLine}>
                          Aggiungi Linea
                      </Button>
                      <Form.Group>
                          <Form.Label>Immagine/Logo</Form.Label>
                          <Form.Control type="file" size="sm" onChange={addImage} accept="image/*" />
                      </Form.Group>
                      <hr />
                      <Form.Group>
                          <Form.Label>Immagine Sfondo</Form.Label>
                          <Form.Control type="file" size="sm" onChange={handleBackgroundImageUpload} accept="image/*" />
                      </Form.Group>
                    </Col>
                    <Col md={7}>
                      <h5>Canvas (A4)</h5>
                      <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} style={{border: '1px solid #ccc'}}>
                        <canvas ref={canvasRef} />
                      </div>
                    </Col>
                    <Col md={3}>
                      <h5>Proprietà</h5>
                      <Form.Group className="mb-3">
                        <Form.Label>Nome Layout</Form.Label>
                        <Form.Control type="text" value={layoutName} onChange={(e) => setLayoutName(e.target.value)} />
                      </Form.Group>
                      <hr />
                      {selectedItem && (
                        <Card>
                          <Card.Body>
                            <Card.Title>Elemento: {selectedItem.type}</Card.Title>
                            {selectedItem.type === 'i-text' && (
                              <>
                                <Form.Group className="mb-2">
                                    <Form.Label>Testo</Form.Label>
                                    <Form.Control as="textarea" name="text" value={selectedItem.text} onChange={handleItemChange} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Dimensione Font</Form.Label>
                                    <Form.Control type="number" name="fontSize" value={selectedItem.fontSize} onChange={handleItemChange} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Font</Form.Label>
                                    <Form.Select name="fontFamily" value={selectedItem.fontFamily} onChange={handleItemChange}>
                                                                <option value="Helvetica">Helvetica</option>
                                                                <option value="Times-Roman">Times New Roman</option>
                                                                <option value="Courier">Courier</option>
                                                            </Form.Select>                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Grassetto</Form.Label>
                                    <Form.Select name="fontWeight" value={selectedItem.fontWeight} onChange={handleItemChange}>
                                        <option value="normal">Normale</option>
                                        <option value="bold">Grassetto</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Colore Testo</Form.Label>
                                    <Form.Control type="color" name="fill" value={selectedItem.fill} onChange={handleItemChange} />
                                </Form.Group>
                              </>
                            )}
                            {(selectedItem.type === 'rect' || selectedItem.type === 'line') && (
                              <>
                                <Form.Group className="mb-2">
                                    <Form.Label>Larghezza</Form.Label>
                                    <Form.Control type="number" name="width" value={Math.round(selectedItem.width * selectedItem.scaleX)} onChange={handleItemChange} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Altezza</Form.Label>
                                    <Form.Control type="number" name="height" value={Math.round(selectedItem.height * selectedItem.scaleY)} onChange={handleItemChange} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Spessore Bordo/Linea</Form.Label>
                                    <Form.Control type="number" name="strokeWidth" value={selectedItem.strokeWidth} onChange={handleItemChange} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Colore Bordo/Linea</Form.Label>
                                    <Form.Control type="color" name="stroke" value={selectedItem.stroke} onChange={handleItemChange} />
                                </Form.Group>
                              </>
                            )}
                            {selectedItem.type === 'image' && (
                              <>
                                  <Form.Group className="mb-2">
                                      <Form.Label>Larghezza</Form.Label>
                                      <Form.Control type="number" name="width" value={Math.round(selectedItem.width * selectedItem.scaleX)} onChange={handleItemChange} />
                                  </Form.Group>
                                  <Form.Group className="mb-2">
                                      <Form.Label>Altezza</Form.Label>
                                      <Form.Control type="number" name="height" value={Math.round(selectedItem.height * selectedItem.scaleY)} onChange={handleItemChange} />
                                  </Form.Group>
                              </>
                            )}
                          </Card.Body>
                        </Card>
                      )}          <Button variant="success" className="mt-3 w-100" onClick={handleSave}>
            Salva Layout
          </Button>
        </Col>
      </Row>
    </Container>
  );
}

export default LayoutEditor;